<template>
    <div v-if="documents.length > 0">
        <div style="padding-left: 1rem">
            <h5
                v-if="documentsFromSeller.length > 0"
                class="mt-3 mb-2 text-left"
            >
                <i class="fal fa-folder-open mr-1" style="min-width: 21px"></i>
                Uppladdat av {{ sellerName }}
            </h5>
            <div
                v-for="(doc, docIdx) in documentsFromSeller"
                :key="docIdx"
                class="mb-2"
            >
                <a
                    :href="doc.link.href"
                    :title="doc.link.title"
                    :target="doc.link.target"
                    :download="doc.link.download"
                    :data-toggle="doc.link.dataToggle"
                    :data-target="`#documents-modal-${doc.id}`"
                    class="btn btn-lg w-100 text-left"
                >
                    <i
                        :class="`fal ${doc.link.icon} mr-1`"
                        style="min-width: 21px"
                    ></i>
                    {{ doc.link.name }}
                    <small v-if="doc.link.nameSuffix"
                        ><i> - {{ doc.link.nameSuffix }}</i>
                    </small>
                </a>
            </div>
        </div>

        <div style="padding-left: 1rem">
            <h5
                v-if="documentsFromCustomer.length > 0"
                class="mt-3 mb-2 text-left"
            >
                <i class="fal fa-folder-open mr-1" style="min-width: 21px"></i>
                Uppladdat av {{ customerName }}
            </h5>

            <div
                v-for="(doc, docIdx) in documentsFromCustomer"
                :key="docIdx"
                class="mb-2"
            >
                <a
                    :href="doc.link.href"
                    :title="doc.link.title"
                    :target="doc.link.target"
                    :download="doc.link.download"
                    :data-toggle="doc.link.dataToggle"
                    :data-target="`#documents-modal-${doc.id}`"
                    class="btn btn-lg w-100 text-left"
                >
                    <i
                        :class="`fal ${doc.link.icon} mr-1`"
                        style="min-width: 21px"
                    ></i>
                    {{ doc.link.name }}
                    <small v-if="doc.link.nameSuffix"
                        ><i> - {{ doc.link.nameSuffix }}</i>
                    </small>
                </a>
            </div>
        </div>

        <Teleport v-if="!disablePdfViewer" to="body">
            <documents-modal
                v-for="(doc, idx) in unsignedDocuments"
                :key="`doc-${idx}`"
                :id="`documents-modal-${doc.id}`"
                :print-url="
                    route('quoteDocuments.print', {
                        quoteKey: quoteKey,
                        id: doc.id,
                    })
                "
                :quote-key="quoteKey"
                :main-pdf-name="doc.name"
                :main-pdf-url="doc.pdf_url"
                :main-pdf-id="doc.id"
                :main-pdf-is-pdf="doc.is_pdf"
                :is-signed="!doc.signature_requested || doc.is_signed"
                :is-post-sale="true"
                :company-color="companyColor"
                :sign-copy="$t('sign.move_forward')"
                sign-modal-target="#bankid-documents"
                @selectedForSigning="
                    selectedIdx = documentsForBankId.findIndex(
                        (docForBankId) => docForBankId.id == doc.id
                    )
                "
            ></documents-modal>

            <bank-id-modal
                v-if="documentsForBankId.length > 0"
                id="bankid-documents"
                :app-url="appUrl"
                :quote-key="quoteKey"
                :documents="documentsForBankId"
                :initially-selected="selectedIdx"
                :disable-pdf-viewer="disablePdfViewer"
                :sign-component-name="signComponentName"
                :sign-component-signee-name="signComponentSigneeName"
                :sign-component-signature-button-label="
                    signComponentSignatureButtonLabel
                "
                :sign-component-item-description="signComponentItemDescription"
            ></bank-id-modal>
        </Teleport>
    </div>
</template>

<style lang="scss" scoped>
a:focus {
    outline: none;
    box-shadow: unset;
}
</style>

<script setup>
import { route } from "ziggy";
import { computed, ref } from "vue";
import { v4 as uuidv4 } from "uuid";
import DocumentsModal from "../modals/documents.vue";
import BankIdModal from "./modals/bankid.vue";
const props = defineProps({
    appUrl: String,
    quoteKey: String,
    documents: Array,
    signComponentName: {
        type: String,
        required: true,
    },
    signComponentSigneeName: {
        type: String,
        default: null,
    },
    signComponentSignatureButtonLabel: {
        type: String,
        required: true,
    },
    signComponentItemDescription: {
        type: String,
        default: null,
    },
    disablePdfViewer: Boolean,
    companyColor: {
        type: String,
        default: "",
    },
    customerName: {
        type: String,
        default: null,
    },
    sellerName: {
        type: String,
        default: null,
    },
});
const unsignedDocuments = computed(() => props.documents.filter((doc) => !doc.is_signed));

function documentLink(doc) {
    const type = doc.is_pdf ? "-pdf" : doc.is_image ? "-image" : "";
    let link;

    if (doc.id === 0) {
        link = {
            icon: "fa-file-contract",
            title: "Signerat",
            nameSuffix: null,
            href: `/signed/${props.quoteKey}`,
            download: null,
            target: null,
            dataToggle: null,
        };
    } else if (doc.is_signed) {
        link = {
            icon: "fa-file-contract",
            title: "Signerat",
            nameSuffix: null,
            href: `/signed/${props.quoteKey}/documents/${doc.id}`,
            download: null,
            target: null,
            dataToggle: null,
        };
    } else if (props.disablePdfViewer || doc.is_download) {
        link = {
            icon: doc.signature_requested
                ? "fa-file-signature"
                : `fa-file${type}`,
            title: doc.signature_requested ? "Väntar på signatur" : doc.name,
            nameSuffix: doc.signature_requested ? "Väntar på signatur" : null,
            href: doc.pdf_url,
            download: doc.is_download ? doc.name_with_extension : null,
            target: "_blank",
            dataToggle: null,
        };
    } else {
        link = {
            icon: doc.signature_requested
                ? "fa-file-signature"
                : `fa-file${type}`,
            title: doc.signature_requested ? "Väntar på signatur" : doc.name,
            nameSuffix: doc.signature_requested ? "Väntar på signatur" : null,
            href: "#",
            download: null,
            target: null,
            dataToggle: "modal",
        };
    }

    return { ...link, name: doc.name_with_extension };
}

const documents = props.documents.map((doc) => ({
    ...doc,
    link: documentLink(doc),
}));
const selectedIdx = ref(0);
const documentsForBankId = ref(
    documents.filter(
        (doc) =>
            !doc.is_customer_upload && doc.signature_requested && !doc.is_signed
    )
);
const documentsFromSeller = ref(
    documents.filter((doc) => !doc.is_customer_upload)
);
const documentsFromCustomer = ref(
    documents.filter((doc) => doc.is_customer_upload)
);
const uuid = ref(uuidv4());
</script>
